import { isValid } from 'date-fns'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DraftHeroImage } from './draft-hero-image'
import { DraftSeoCard } from './draft-seo-card'
import { ImageCandidatesGrid } from './image-candidates-grid'
import { formatDateTime, formatDateOnly } from '@/lib/utils/date'
import { computeBodyStats } from '@/lib/utils/markdown-stats'
import type { BlogDraft } from '@/lib/api/types'

function safeFormatDateTime(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  return isValid(d) ? formatDateTime(value) : null
}

function safeFormatDate(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  return isValid(d) ? formatDateOnly(value) : null
}

interface DraftSidebarProps {
  draft: BlogDraft
  agentId: string
  draftId: string
  requireDraftStatus: (action: () => void) => void
  imageOpsPending: boolean
}

export function DraftSidebar({ draft, agentId, draftId, requireDraftStatus, imageOpsPending }: DraftSidebarProps) {
  const stats = computeBodyStats(draft.body)

  return (
    <div className="md:sticky md:top-4 md:self-start space-y-4">
      <DraftHeroImage url={draft.heroImageUrl ?? draft.selectedImageUrl} alt={draft.heroImageAlt} />

      <ImageCandidatesGrid
        agentId={agentId}
        draftId={draftId}
        draftTitle={draft.title}
        requireDraftStatus={requireDraftStatus}
        imageOpsPending={imageOpsPending}
      />

      {draft.seo && <DraftSeoCard seo={draft.seo} />}

      {/* Phase 30 (v4.3) Sources pointer — D-08/D-09. Slots between SEO and
          Content Stats. Hidden when sources is undefined or empty (D-11). */}
      {draft.sources && draft.sources.length > 0 && (
        <Card>
          <CardContent className="p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Sources</span>
              <Badge variant="secondary">
                {draft.sources.length === 1
                  ? '1 source'
                  : `${draft.sources.length} sources`}
              </Badge>
            </div>
            <a
              href="#sources"
              className="text-sm text-primary hover:underline"
            >
              Jump to sources
            </a>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 space-y-2">
          <p className="text-sm font-medium mb-1">Content Stats</p>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Words</span>
            <span>{stats.words.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Paragraphs</span>
            <span>{stats.paragraphs}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Sentences</span>
            <span>{stats.sentences}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Reading time</span>
            <span>{stats.readingTimeMin} min</span>
          </div>
          {stats.headings > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Headings</span>
              <span>{stats.headings}</span>
            </div>
          )}
          {stats.images > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Images</span>
              <span>{stats.images}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Created</span>
            <span>{safeFormatDateTime(draft.createdAt) ?? '—'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Updated</span>
            <span>{safeFormatDateTime(draft.updatedAt) ?? '—'}</span>
          </div>
          {safeFormatDate(draft.scheduledAt) && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Scheduled</span>
              <span>{safeFormatDate(draft.scheduledAt)}</span>
            </div>
          )}
          {safeFormatDate(draft.publishedAt) && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Published</span>
              <span>{safeFormatDate(draft.publishedAt)}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
